import type {
  ChatStatus,
  FileUIPart,
  SourceDocumentUIPart,
  TextUIPart,
} from "ai";
import { useEffect, useMemo, useState, type ReactNode, type Ref } from "react";

/**
 * The prompt input + footer used by every chat surface.
 *
 * Has two flavors switched at mount time:
 *
 *   - PlainComposer: the bare ai-elements PromptInput. Used where there is no
 *     workspace yet (an onboarding interview).
 *   - ContextComposer: adds the file-context affordances: removable chips
 *     for the auto-pinned open file and each `@`-mention, and `@`-autocomplete in the
 *     textarea. Files come from the host's `FileContextProvider`, which is read
 *     at submit time to snapshot their contents.
 *
 * Branching here (rather than wrapping every callsite) means a host whose file
 * store isn't ready yet simply passes no provider.
 */
import {
  Attachment,
  AttachmentPreview,
  AttachmentRemove,
  Attachments,
} from "../ai-elements/attachments";
import {
  PromptInput,
  PromptInputActionAddAttachments,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuTrigger,
  PromptInputBody,
  PromptInputFooter,
  PromptInputHeader,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
  usePromptInputAttachments,
  type PromptInputMessage,
} from "../ai-elements/prompt-input";
import { submitMode } from "./composer-controls";
import { ContextChips } from "./ContextChips";
import { formatContextBlock, type ContextFile } from "./file-mentions";
import { FileMentionTextarea } from "./FileMentionTextarea";
import type {
  ContextRef,
  FileContextProvider,
  SentMessage,
  UploadAttachment,
} from "./types";

export type { SentMessage } from "./types";

/**
 * Default ceiling for one attachment. The binding limit is the model provider's
 * per-file cap — OpenAI accepts up to 20 MB per image and 32 MB per PDF — so
 * this is the tighter of the two. A host that stores attachments somewhere with
 * its own limit passes its own.
 */
export const DEFAULT_MAX_FILE_SIZE = 20 * 1024 * 1024;

/**
 * Externalize attachments before they enter the chat message.
 *
 * The ai-elements PromptInput hands us attachments as base64 `data:` URL
 * FileUIParts. Sent as-is, those multi-MB blobs ride in the chat POST body and
 * get persisted with the message. When the host supplies an uploader we hand it
 * the bytes and swap `url` for whatever reference it returns.
 *
 * Resilient by design: any failure leaves that part's original `data:` URL
 * untouched (a host is expected to have a server-side backstop). We never block
 * or fail the send.
 */
async function externalizeAttachmentParts(
  files: FileUIPart[],
  upload: UploadAttachment | undefined,
): Promise<FileUIPart[]> {
  if (!upload) return files;
  return Promise.all(
    files.map(async (part) => {
      if (!part.url.startsWith("data:")) return part;
      try {
        const blob = await (await fetch(part.url)).blob();
        const url = await upload(blob, part);
        return url ? { ...part, url } : part;
      } catch {
        return part;
      }
    }),
  );
}

export interface ComposerProps {
  onSend: (message: SentMessage) => void;
  status: ChatStatus;
  /**
   * Abort the in-flight turn. When provided, the submit control becomes a
   * Stop button while a turn streams and the composer is empty. Left unset on
   * surfaces with no turn to stop (e.g. a new-chat composer).
   */
  onStop?: () => void;
  placeholder: string;
  textareaDisabled: boolean;
  submitDisabled: boolean;
  /** Show the attachment affordance (file picker + paste + drop). */
  allowAttachments: boolean;
  /** Media types the picker accepts, as an `accept` attribute value. */
  attachmentAccept?: string;
  maxFileSize?: number;
  /** Where the composer's bytes go. Omit to send `data:` URLs as-is. */
  uploadAttachment?: UploadAttachment;
  /**
   * Enable per-message file context — chip strip + `@`-autocomplete. Omit on
   * surfaces with no workspace behind them.
   */
  fileContext?: FileContextProvider | null;
  mentionPlacement?: "above" | "below";
  /** Path of the file the user is currently viewing — auto-attached as context. */
  openFilePath: string | null;
  composerRef?: Ref<HTMLTextAreaElement>;
  /** Focus the textarea on mount (a just-created chat). */
  autoFocus?: boolean;
  /**
   * Host controls in the footer, left of the send button — a model picker, an
   * effort menu. A render prop because the row's enabled state is the
   * composer's to decide (offline, disabled), not the host's to guess.
   */
  footerExtras?: (state: { disabled: boolean }) => ReactNode;
  footerLeading?: ReactNode;
  submitLabel?: ReactNode;
  submitAriaLabel?: string;
  disableEmptySubmit?: boolean;
  className?: string;
}

export function Composer(props: ComposerProps) {
  return props.fileContext ? (
    <ContextComposer {...props} fileContext={props.fileContext} />
  ) : (
    <PlainComposer {...props} />
  );
}

function PlainComposer({
  onSend,
  status,
  onStop,
  placeholder,
  textareaDisabled,
  submitDisabled,
  allowAttachments,
  attachmentAccept,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  uploadAttachment,
  composerRef,
  autoFocus,
  footerExtras,
  footerLeading,
  submitLabel,
  submitAriaLabel,
  disableEmptySubmit,
  className,
}: ComposerProps) {
  // Tracks whether the textarea is non-empty so the footer can flip the
  // submit control between Stop (busy + empty) and Send/queue. The PromptInput
  // textarea is uncontrolled, so we mirror the trimmed length here and reset
  // it on submit (the form resets itself, which fires no change event).
  const [hasText, setHasText] = useState(false);

  const handleSubmit = async (msg: PromptInputMessage) => {
    const text = msg.text.trim();
    if (!text && msg.files.length === 0) return;
    if (textareaDisabled) return;
    const files = await externalizeAttachmentParts(msg.files, uploadAttachment);
    onSend({ text, files });
    setHasText(false);
  };

  return (
    <PromptInput
      className={className}
      onSubmit={handleSubmit}
      accept={allowAttachments ? attachmentAccept : undefined}
      multiple={allowAttachments}
      maxFileSize={maxFileSize}
      onError={(err) => window.alert(err.message)}
    >
      {allowAttachments ? <AttachmentsHeader /> : null}
      <PromptInputBody>
        <PromptInputTextarea
          ref={composerRef}
          placeholder={placeholder}
          disabled={textareaDisabled}
          autoFocus={autoFocus}
          onChange={(e) => setHasText(e.currentTarget.value.trim().length > 0)}
        />
      </PromptInputBody>
      <ComposerFooter
        allowAttachments={allowAttachments}
        textareaDisabled={textareaDisabled}
        submitDisabled={submitDisabled}
        status={status}
        hasText={hasText}
        onStop={onStop}
        footerExtras={footerExtras}
        footerLeading={footerLeading}
        submitLabel={submitLabel}
        submitAriaLabel={submitAriaLabel}
        disableEmptySubmit={disableEmptySubmit}
      />
    </PromptInput>
  );
}

function ContextComposer({
  onSend,
  status,
  onStop,
  placeholder,
  textareaDisabled,
  submitDisabled,
  allowAttachments,
  attachmentAccept,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  uploadAttachment,
  fileContext,
  mentionPlacement,
  openFilePath,
  composerRef,
  autoFocus,
  footerExtras,
  footerLeading,
  submitLabel,
  submitAriaLabel,
  disableEmptySubmit,
  className,
}: ComposerProps & { fileContext: FileContextProvider }) {
  const allPaths = fileContext.paths;

  const [mentionedPaths, setMentionedPaths] = useState<string[]>([]);
  // See PlainComposer: mirrors textarea emptiness so the footer can flip the
  // submit control between Stop and Send/queue.
  const [hasText, setHasText] = useState(false);

  // Drop any mention whose file disappears from the workspace (rename,
  // delete) so the chip strip never shows a dangling reference.
  useEffect(() => {
    const available = new Set(allPaths);
    setMentionedPaths((prev) => {
      const next = prev.filter((p) => available.has(p));
      return next.length === prev.length ? prev : next;
    });
  }, [allPaths]);

  // The open file rides along until its chip is removed. Navigating to
  // another file pins that one, so a dismissal never outlives its file.
  const [dismissedOpenPath, setDismissedOpenPath] = useState<string | null>(
    null,
  );
  const pinnedPath = openFilePath === dismissedOpenPath ? null : openFilePath;

  // Opening a file clears any dismissal and drops a duplicate mention chip.
  useEffect(() => {
    setDismissedOpenPath(null);
    setMentionedPaths((prev) => prev.filter((p) => p !== openFilePath));
  }, [openFilePath]);

  const contextRefs = useMemo<ContextRef[]>(() => {
    const out: ContextRef[] = [];
    if (pinnedPath) out.push({ path: pinnedPath, source: "open" });
    for (const p of mentionedPaths) out.push({ path: p, source: "mention" });
    return out;
  }, [pinnedPath, mentionedPaths]);

  const removeContextRef = (ref: ContextRef) =>
    ref.source === "open"
      ? setDismissedOpenPath(ref.path)
      : setMentionedPaths((prev) => prev.filter((p) => p !== ref.path));

  const excludePaths = useMemo(
    () => contextRefs.map((r) => r.path),
    [contextRefs],
  );

  const handleSubmit = async (msg: PromptInputMessage) => {
    const text = msg.text.trim();
    if (!text && msg.files.length === 0) return;
    if (textareaDisabled) return;
    let contextParts: (TextUIPart | SourceDocumentUIPart)[] | undefined;
    if (contextRefs.length > 0) {
      const snapshots: ContextFile[] = [];
      for (const ref of contextRefs) {
        const content = await fileContext.read(ref.path);
        if (content !== null) snapshots.push({ path: ref.path, content });
      }
      if (snapshots.length > 0) {
        const block = formatContextBlock(snapshots);
        const sourceParts: SourceDocumentUIPart[] = snapshots.map((s) => ({
          type: "source-document",
          sourceId: s.path,
          mediaType: "text/markdown",
          title: s.path,
        }));
        contextParts = [{ type: "text", text: block }, ...sourceParts];
      }
    }
    const uploadedFiles = await externalizeAttachmentParts(
      msg.files,
      uploadAttachment,
    );
    onSend({ text, files: uploadedFiles, contextParts });
    // Mentions are per-message; clear them after a successful submit. The
    // open-file chip (and its dismissal) follows `openFilePath` instead.
    setMentionedPaths([]);
    setHasText(false);
  };

  return (
    <PromptInput
      className={className}
      onSubmit={handleSubmit}
      accept={allowAttachments ? attachmentAccept : undefined}
      multiple={allowAttachments}
      maxFileSize={maxFileSize}
      onError={(err) => window.alert(err.message)}
    >
      <ContextHeader
        contextRefs={contextRefs}
        allowAttachments={allowAttachments}
        onRemove={removeContextRef}
      />
      <PromptInputBody>
        <FileMentionTextarea
          ref={composerRef}
          placeholder={placeholder}
          disabled={textareaDisabled}
          autoFocus={autoFocus}
          allPaths={allPaths}
          mentionPlacement={mentionPlacement}
          excludePaths={excludePaths}
          onChange={(e) => setHasText(e.currentTarget.value.trim().length > 0)}
          onMentionFile={(path) =>
            setMentionedPaths((prev) =>
              prev.includes(path) ? prev : [...prev, path],
            )
          }
        />
      </PromptInputBody>
      <ComposerFooter
        allowAttachments={allowAttachments}
        textareaDisabled={textareaDisabled}
        submitDisabled={submitDisabled}
        status={status}
        hasText={hasText}
        onStop={onStop}
        footerExtras={footerExtras}
        footerLeading={footerLeading}
        submitLabel={submitLabel}
        submitAriaLabel={submitAriaLabel}
        disableEmptySubmit={disableEmptySubmit}
      />
    </PromptInput>
  );
}

interface ComposerFooterProps {
  allowAttachments: boolean;
  textareaDisabled: boolean;
  submitDisabled: boolean;
  status: ChatStatus;
  hasText: boolean;
  onStop?: () => void;
  footerExtras?: (state: { disabled: boolean }) => ReactNode;
  footerLeading?: ReactNode;
  submitLabel?: ReactNode;
  submitAriaLabel?: string;
  disableEmptySubmit?: boolean;
}

function ComposerFooter({
  allowAttachments,
  textareaDisabled,
  submitDisabled,
  status,
  hasText,
  onStop,
  footerExtras,
  footerLeading,
  submitLabel,
  submitAriaLabel,
  disableEmptySubmit,
}: ComposerFooterProps) {
  // Mirror the submit guard (text OR files): a staged attachment with no typed
  // text is still sendable, so it must flip the control to Send — otherwise it
  // stays a Stop button mid-turn and the attachment can't be queued.
  const attachments = usePromptInputAttachments();
  const hasContent = hasText || attachments.files.length > 0;
  const mode = submitMode(status, hasContent);
  const busy = status === "submitted" || status === "streaming";
  return (
    <PromptInputFooter>
      <PromptInputTools>
        {allowAttachments ? (
          <PromptInputActionMenu>
            <PromptInputActionMenuTrigger disabled={textareaDisabled} />
            <PromptInputActionMenuContent>
              <PromptInputActionAddAttachments label="Add file" />
            </PromptInputActionMenuContent>
          </PromptInputActionMenu>
        ) : null}
        {footerLeading}
      </PromptInputTools>
      {/* Grouped: the footer is `justify-between`, so a third child would
          float to the middle instead of trailing the send button. */}
      <div className="flex items-center gap-1">
        {footerExtras?.({ disabled: textareaDisabled })}
        {mode === "stop" ? (
          // Busy + empty: the control shows the streaming/submitted indicator
          // and, when the surface supplied `onStop`, clicking it aborts the turn.
          <PromptInputSubmit
            status={status}
            onStop={onStop}
            disabled={submitDisabled}
          />
        ) : (
          // Send/queue: force the plain send arrow even mid-turn (passing
          // `status` would morph it into the stop square) so a typed message
          // can be queued.
          <PromptInputSubmit
            status={busy ? undefined : status}
            disabled={submitDisabled || (disableEmptySubmit && !hasContent)}
            size={submitLabel ? "sm" : undefined}
            aria-label={submitAriaLabel}
          >
            {submitLabel}
          </PromptInputSubmit>
        )}
      </div>
    </PromptInputFooter>
  );
}

function AttachmentsList() {
  const attachments = usePromptInputAttachments();
  return (
    <Attachments variant="inline">
      {attachments.files.map((file) => (
        <Attachment
          key={file.id}
          data={file}
          onRemove={() => attachments.remove(file.id)}
        >
          <AttachmentPreview />
          <AttachmentRemove />
        </Attachment>
      ))}
    </Attachments>
  );
}

function AttachmentsHeader() {
  const attachments = usePromptInputAttachments();
  if (attachments.files.length === 0) return null;
  return (
    <PromptInputHeader>
      <AttachmentsList />
    </PromptInputHeader>
  );
}

interface ContextHeaderProps {
  contextRefs: ContextRef[];
  allowAttachments: boolean;
  onRemove: (ref: ContextRef) => void;
}

function ContextHeader({
  contextRefs,
  allowAttachments,
  onRemove,
}: ContextHeaderProps) {
  const attachments = usePromptInputAttachments();
  const hasAttachments = allowAttachments && attachments.files.length > 0;
  if (contextRefs.length === 0 && !hasAttachments) return null;
  return (
    <PromptInputHeader>
      {contextRefs.length > 0 ? (
        <ContextChips refs={contextRefs} onRemove={onRemove} />
      ) : null}
      {hasAttachments ? <AttachmentsList /> : null}
    </PromptInputHeader>
  );
}
